/**
 * Workspace, membership and account data.
 *
 * `useOrgDetail` is the one call that answers "what does this plan allow" — brand allowance,
 * member allowance and the white-label flag all come from it, so brands, billing and the
 * white-label panel read this hook rather than counting for themselves.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { Member, OrgRole, SessionUser } from '@ftk-connector/shared'

import { apiV1 } from '../../lib/api'
import { qk } from '../../lib/query'

/**
 * `GET /api/v1/orgs/current`. Composed by the backend in `modules/orgs/service.ts`; it is not a
 * `@ftk-connector/shared` export, so the wire shape is declared here rather than re-derived.
 */
export interface OrgDetail {
  id: string
  name: string
  slug: string
  timezone: string
  locale: string
  currency: string
  suspended: boolean
  createdAt: string
  role: OrgRole
  planCode: string
  planName: string
  /** Plan + packs + referral bonus, or a superadmin override alone. What the server enforces. */
  brandAllowance: number
  brandsUsed: number
  extraBrandPacks: number
  /**
   * The part of `brandAllowance` that active referrals earned. Counted live by the server on
   * every read, so it falls away on its own when a referred workspace stops paying — see
   * `features/referrals`.
   */
  referralBonusBrands: number
  brandLimitOverride: number | null
  /** `null` means unlimited — never collapse it into a number. */
  memberLimit: number | null
  memberCount: number
  whitelabelEnabled: boolean
  approvalsEnabled: boolean
  rolesEnabled: boolean
  subscription: {
    status: string
    interval: string
    currentPeriodEnd: string | null
    cancelAtPeriodEnd: boolean
  } | null
}

/** The active workspace is implied by `X-Org-Id`; switching one clears the whole cache. */
export const ORG_DETAIL_KEY = ['pd', 'org', 'current'] as const

export const useOrgDetail = (): UseQueryResult<OrgDetail> =>
  useQuery({
    queryKey: ORG_DETAIL_KEY,
    queryFn: () => apiV1<OrgDetail>('/orgs/current'),
  })

/* ── team ─────────────────────────────────────────────────────────────────── */

export const useMembers = (orgId: string | null): UseQueryResult<Member[]> =>
  useQuery({
    queryKey: qk.members(orgId ?? 'none'),
    queryFn: () => apiV1<Member[]>('/orgs/current/members'),
    enabled: orgId !== null,
  })

export interface InviteMemberInput {
  email: string
  name?: string
  /** The rank. Ignored when `roleId` names a custom role — that role's own rank wins. */
  role: OrgRole
  /** A custom role of this workspace. `null`/absent means the built-in role named by `role`. */
  roleId?: string | null
  brandScope?: number[] | null
}

export interface UpdateMemberInput {
  userId: string
  role?: OrgRole
  /**
   * Assign a custom role, or `null` to put the membership back on its built-in rank. Never send a
   * non-null `roleId` together with `role`: a custom role carries its own rank and the API refuses
   * the pairing rather than guessing which of the two was meant.
   */
  roleId?: string | null
  brandScope?: number[] | null
}

/** Every membership write invalidates the roster and the workspace's member count together. */
const useMemberMutation = <TInput, TResult>(
  orgId: string | null,
  run: (input: TInput) => Promise<TResult>,
): UseMutationResult<TResult, unknown, TInput> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.members(orgId ?? 'none') })
      void queryClient.invalidateQueries({ queryKey: ORG_DETAIL_KEY })
    },
  })
}

export const useInviteMember = (
  orgId: string | null,
): UseMutationResult<Member, unknown, InviteMemberInput> =>
  useMemberMutation(orgId, (input: InviteMemberInput) =>
    apiV1<Member>('/orgs/current/members', { method: 'POST', json: input }),
  )

export const useUpdateMember = (
  orgId: string | null,
): UseMutationResult<Member, unknown, UpdateMemberInput> =>
  useMemberMutation(orgId, ({ userId, ...rest }: UpdateMemberInput) =>
    apiV1<Member>(`/orgs/current/members/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      json: rest,
    }),
  )

export const useRemoveMember = (
  orgId: string | null,
): UseMutationResult<{ ok: true; userId: string }, unknown, string> =>
  useMemberMutation(orgId, (userId: string) =>
    apiV1<{ ok: true; userId: string }>(`/orgs/current/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),
  )

/* ── account ──────────────────────────────────────────────────────────────── */

export interface ProfileInput {
  name?: string
  avatarUrl?: string | null
  timezone?: string
  locale?: string
}

export const useUpdateProfile = (): UseMutationResult<SessionUser, unknown, ProfileInput> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ProfileInput) => apiV1<SessionUser>('/users/me', { method: 'PATCH', json: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.session() })
    },
  })
}

export interface PasswordInput {
  currentPassword: string
  newPassword: string
}

export const useChangePassword = (): UseMutationResult<{ ok: true }, unknown, PasswordInput> =>
  useMutation({
    mutationFn: (input: PasswordInput) =>
      apiV1<{ ok: true }>('/users/me/password', { method: 'PUT', json: input }),
  })

export const useRegenerateApiToken = (): UseMutationResult<{ userToken: string }, unknown, void> =>
  useMutation({
    mutationFn: () => apiV1<{ userToken: string }>('/users/me/token', { method: 'POST' }),
  })

/* ── workspace ────────────────────────────────────────────────────────────── */

export interface WorkspaceInput {
  name?: string
  timezone?: string
  locale?: string
  currency?: string
}

export const useUpdateWorkspace = (): UseMutationResult<OrgDetail, unknown, WorkspaceInput> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: WorkspaceInput) =>
      apiV1<OrgDetail>('/orgs/current', { method: 'PATCH', json: input }),
    onSuccess: (detail) => {
      queryClient.setQueryData(ORG_DETAIL_KEY, detail)
    },
  })
}
